import { DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SettingOutlined, ThunderboltOutlined, UploadOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { eventLabel, statusLabel } from "@wms/domain";
import { describeChanges } from "./describeChanges";
import type { ActionMeta, EntityMeta, FieldMeta } from "@wms/domain";
import { App, Button, Checkbox, Dropdown, Form, Input, InputNumber, Modal, Popconfirm, Popover, Select, Space, Switch, Table, Tag } from "antd";
import type { TableProps } from "antd";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, describeError, errorMessage } from "../api/client";
import type { Page } from "../api/client";
import { downloadFile } from "../api/download";
import { formatDate, formatDateTime } from "../api/format";
import { useAuthStore } from "../auth/store";
import { BarcodeButton, codeTarget } from "./BarcodeButton";
import { ImportModal } from "./ImportModal";
import { OPERATION_FIELDS, OperationLegend, OperationTag, mixesOperations, operationOf, useDocumentOperation } from "./OperationTag";
import { EntityLabel, RefLabel, RefSelect } from "./RefSelect";

export type Row = Record<string, unknown> & { id: string };

const HIDDEN_IN_LIST = new Set(["json", "password", "multiref"]);
const AUDIT_COLUMNS = new Set(["created_by", "updated_by", "updated_at"]);

export function renderCell(field: FieldMeta, value: unknown) {
  if (value === null || value === undefined) return "";
  // R31 «цвета операций»: a receipt, a shipment, a move or a write-off is told by colour, icon and word together
  const operation = OPERATION_FIELDS.has(field.name) ? operationOf(value) : null;
  if (operation && (field.type === "enum" || field.type === "entity" || field.type === "string")) {
    const label =
      field.type === "entity" ? <EntityLabel name={String(value)} /> : field.options?.find((o) => o.value === value)?.label ?? String(value);
    return <OperationTag kind={operation}>{label}</OperationTag>;
  }
  switch (field.type) {
    case "boolean":
      return value ? <Tag color="green">Да</Tag> : <Tag>Нет</Tag>;
    case "datetime":
      return formatDateTime(value);
    case "date":
      return formatDate(value);
    case "ref":
      return <RefLabel entity={field.ref!} id={String(value)} />;
    case "refcode":
      return <RefLabel entity={field.ref!} id={String(value)} valueField="code" />;
    case "entity":
      return <EntityLabel name={String(value)} />;
    case "enum":
      // QA1101-B01: an event type the list of options does not know yet is named in words, not by its code
      return field.options?.find((o) => o.value === value)?.label ?? (field.name === "type" && /^[a-z_]+\.[a-z_]+$/.test(String(value)) ? eventLabel(String(value)) : String(value));
    case "multienum":
      return (Array.isArray(value) ? value : [])
        .map((v) => field.options?.find((o) => o.value === v)?.label ?? String(v))
        .join(", ");
    default:
      if (field.name === "status" || field.name === "last_status") return statusLabel(value);
      if (field.name === "type" && /^[a-z_]+\.[a-z_]+$/.test(String(value))) return eventLabel(String(value));
      return String(value);
  }
}

interface Props {
  entity: string;
  /** Extra filter, e.g. child list inside a parent card. */
  baseFilter?: Record<string, unknown>;
  /** Hidden columns (the parent fk inside a child tab). */
  hideFields?: string[];
  /** Query string appended when creating a new row (prefill of the parent fk). */
  createParams?: Record<string, string>;
  embedded?: boolean;
  /** UXW-39: another way to add rows shown next to «Создать» (the stock picker of an order); «Создать» stays. */
  toolbarExtra?: React.ReactNode;
}

type Views = Record<string, { columns?: string[] }>;

export function EntityTable({ entity, baseFilter, hideFields = [], createParams, embedded, toolbarExtra }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const can = useAuthStore((s) => s.can);
  const savePreferences = useAuthStore((s) => s.savePreferences);
  const savedColumns = useAuthStore((s) => (s.me?.preferences?.views as Views | undefined)?.[entity]?.columns);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(embedded ? 20 : 50);
  const [sort, setSort] = useState<string | undefined>();
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<React.Key[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkField, setBulkField] = useState<string | undefined>();
  const [bulkForm] = Form.useForm();
  // REQ-08: one document action for many selected rows, with a per-row outcome that hides nothing
  const [bulkAction, setBulkAction] = useState<ActionMeta | null>(null);
  const [bulkResults, setBulkResults] = useState<{ id: string; label: string; ok: boolean; message: string }[] | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const meta = useQuery({ queryKey: ["meta", entity], queryFn: () => api.get<EntityMeta>(`/${entity}/meta`) });
  const operationOfRow = useDocumentOperation(entity);
  const list = useQuery({
    queryKey: [entity, "list", { page, size, sort, q, baseFilter }],
    queryFn: () => api.get<Page<Row>>(`/${entity}`, { page, size, sort, q, filter: baseFilter }),
    placeholderData: keepPreviousData,
    enabled: !!meta.data,
  });

  const bulkDelete = useMutation({
    mutationFn: (ids: React.Key[]) => api.post<{ deleted: number }>(`/${entity}/bulk-delete`, { ids }),
    onSuccess: (r) => {
      message.success(`Удалено: ${r.deleted}`);
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: [entity] });
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const bulkUpdate = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post<{ updated: number }>(`/${entity}/bulk-update`, { ids: selected, data }),
    onSuccess: (r) => {
      message.success(`Изменено: ${r.updated}`);
      setBulkOpen(false);
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: [entity] });
    },
    onError: (e) => message.error(errorMessage(e)),
  });

  /** Columns that can be shown at all, and the ones shown now (saved per user, or the first few by default). */
  const listable = useMemo(
    () =>
      (meta.data?.fields ?? []).filter(
        (f) =>
          !f.hidden &&
          // BUG-H-001: the history shows what changed («статус: Собран → Упакован, задание T-000117»), not only who
          (!HIDDEN_IN_LIST.has(f.type) || (entity === "audit" && f.name === "changes")) &&
          f.name !== "id" &&
          !AUDIT_COLUMNS.has(f.name) &&
          !hideFields.includes(f.name),
      ),
    [meta.data, hideFields, entity],
  );
  const visibleNames = useMemo(() => {
    if (savedColumns?.length && !embedded) return savedColumns.filter((n) => listable.some((f) => f.name === n));
    const preferred = (meta.data?.list_fields ?? []).filter((n) => listable.some((f) => f.name === n));
    if (preferred.length) return preferred.slice(0, embedded ? 7 : 9);
    return listable.slice(0, embedded ? 7 : 9).map((f) => f.name);
  }, [savedColumns, listable, embedded, meta.data]);

  const columns = useMemo<TableProps<Row>["columns"]>(
    () =>
      visibleNames
        .map((name) => listable.find((f) => f.name === name)!)
        .filter(Boolean)
        .map((f) => ({
          key: f.name,
          dataIndex: f.name,
          title: f.label,
          sorter: f.type !== "ref",
          ellipsis: f.name !== "changes",
          render: (value: unknown, record: Row) => {
            if (entity === "audit" && f.name === "changes") return describeChanges(value, String(record.entity ?? ""));
            // a product, a place or a cargo unit: its code for the terminal is one button away (ШК для ТСД)
            const code = codeTarget(entity, f, value, record);
            return code ? (
              <>
                <BarcodeButton {...code} />
                {renderCell(f, value)}
              </>
            ) : (
              renderCell(f, value)
            );
          },
        })),
    [visibleNames, listable, entity],
  );

  const onChange: TableProps<Row>["onChange"] = (pagination, _filters, sorter) => {
    setPage(pagination.current ?? 1);
    setSize(pagination.pageSize ?? 50);
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (s?.order && typeof s.field === "string") setSort(`${s.order === "descend" ? "-" : ""}${s.field}`);
    else setSort(undefined);
  };

  const saveColumns = (names: string[] | null) => {
    savePreferences({ views: { [entity]: names ? { columns: names } : null } }).catch(() => message.error("Не удалось сохранить колонки"));
  };
  const exportList = (format: "xlsx" | "csv") => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    if (baseFilter) params.set("filter", JSON.stringify(baseFilter));
    params.set("columns", visibleNames.join(","));
    downloadFile(`/${entity}/export.${format}?${params.toString()}`, `${entity}.${format}`).catch((e) => message.error(errorMessage(e)));
  };

  const canWrite = !!meta.data && !meta.data.readonly && can(`${meta.data.permission}.write`);
  // 3D docks: the gates of a dock come from its «Количество ворот», so their list has no «Создать» and no import
  const canCreate = canWrite && meta.data?.creatable !== false;
  const bulkActions = (meta.data?.actions ?? []).filter((a) => a.kind === "simple" && can(a.permission ?? `${meta.data!.permission}.write`));
  const rowLabelOf = (row: Row | undefined) => String(row?.number ?? row?.code ?? row?.name ?? row?.id ?? "");
  const runBulk = async (action: ActionMeta) => {
    const rows = (list.data?.items ?? []).filter((r) => selected.includes(r.id));
    setBulkRunning(true);
    const results: { id: string; label: string; ok: boolean; message: string }[] = [];
    for (const row of rows) {
      const status = String(row.status ?? "");
      if (action.statuses && !action.statuses.includes(status)) {
        results.push({ id: row.id, label: rowLabelOf(row), ok: false, message: `пропущено: статус «${statusLabel(status)}» не подходит` });
        continue;
      }
      try {
        const data = await api.post<{ status?: string }>(`/${entity}/${row.id}/${action.code}`);
        results.push({ id: row.id, label: rowLabelOf(row), ok: true, message: data?.status ? `статус: ${statusLabel(data.status)}` : "готово" });
      } catch (e) {
        results.push({ id: row.id, label: rowLabelOf(row), ok: false, message: describeError(e) });
      }
    }
    setBulkRunning(false);
    setBulkAction(null);
    setBulkResults(results);
    queryClient.invalidateQueries({ queryKey: [entity] });
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
    queryClient.invalidateQueries({ queryKey: ["stock"] });
  };
  const createUrl = `/${entity}/new${createParams ? "?" + new URLSearchParams(createParams).toString() : ""}`;
  const editable = listable.filter((f) => !f.readonly && f.type !== "uuid");
  const bulkMeta = editable.find((f) => f.name === bulkField);

  const columnChooser = (
    <div style={{ maxHeight: 360, overflow: "auto", minWidth: 220 }}>
      <Checkbox.Group
        value={visibleNames}
        onChange={(values) => saveColumns(listable.filter((f) => values.includes(f.name)).map((f) => f.name))}
        style={{ display: "flex", flexDirection: "column", gap: 4 }}
        options={listable.map((f) => ({ label: f.label, value: f.name }))}
      />
      <Button size="small" type="link" onClick={() => saveColumns(null)} style={{ paddingLeft: 0, marginTop: 8 }}>
        По умолчанию
      </Button>
    </div>
  );

  return (
    <div>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }} wrap>
        <Space wrap>
          {/* BUG-Z-002: a list the server cannot search gets no search box that would silently ignore the query */}
          {(meta.data?.searchable.length ?? 0) > 0 || meta.data?.search_hint ? (
            <Input.Search
              allowClear
              placeholder={meta.data?.search_hint ?? "Поиск"}
              title={meta.data?.search_hint ?? undefined}
              style={{ width: embedded ? 220 : 320, maxWidth: "calc(100vw - 56px)" }}
              onSearch={(value) => {
                setQ(value);
                setPage(1);
              }}
            />
          ) : null}
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} />
          {!embedded && (
            <Popover content={columnChooser} title="Колонки" trigger="click" placement="bottomLeft">
              <Button icon={<SettingOutlined />}>Колонки</Button>
            </Popover>
          )}
          <Dropdown
            menu={{
              items: [
                { key: "xlsx", label: "Excel (XLSX)", onClick: () => exportList("xlsx") },
                { key: "csv", label: "CSV", onClick: () => exportList("csv") },
              ],
            }}
          >
            <Button icon={<DownloadOutlined />}>Выгрузить</Button>
          </Dropdown>
        </Space>
        <Space wrap>
          {canWrite && selected.length > 0 && bulkActions.length > 0 && (
            <Dropdown
              menu={{ items: bulkActions.map((a) => ({ key: a.code, label: a.label, danger: a.danger, onClick: () => setBulkAction(a) })) }}
            >
              <Button icon={<ThunderboltOutlined />}>Действие с выбранными ({selected.length})</Button>
            </Dropdown>
          )}
          {canWrite && selected.length > 0 && (
            <>
              <Button icon={<EditOutlined />} onClick={() => setBulkOpen(true)}>
                Изменить поле ({selected.length})
              </Button>
              <Popconfirm title={`Удалить выбранные (${selected.length})?`} okText="Удалить" cancelText="Отмена" onConfirm={() => bulkDelete.mutate(selected)}>
                <Button danger icon={<DeleteOutlined />} loading={bulkDelete.isPending}>
                  Удалить выбранные
                </Button>
              </Popconfirm>
            </>
          )}
          {canCreate && !embedded && (
            <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
              Импорт
            </Button>
          )}
          {toolbarExtra}
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(createUrl)}>
              Создать
            </Button>
          )}
        </Space>
      </Space>
      {mixesOperations(entity) && !embedded && <OperationLegend />}
      <Table<Row>
        size={embedded ? "small" : "middle"}
        rowKey="id"
        rowClassName={(row) => {
          const kind = operationOfRow(row, list.data?.items);
          return kind ? `wms-doc wms-doc-${kind}` : "";
        }}
        columns={columns}
        dataSource={list.data?.items ?? []}
        loading={meta.isLoading || list.isLoading}
        onChange={onChange}
        rowSelection={canWrite ? { selectedRowKeys: selected, onChange: setSelected } : undefined}
        pagination={{ current: page, pageSize: size, total: list.data?.total ?? 0, showSizeChanger: true, showTotal: (t) => `Всего: ${t}` }}
        onRow={(row) => ({ onClick: () => navigate(`/${entity}/${row.id}`), style: { cursor: "pointer" } })}
        scroll={{ x: true }}
      />
      {meta.data && (
        <ImportModal entity={entity} label={meta.data.label_plural} open={importOpen} onClose={() => setImportOpen(false)} />
      )}
      <Modal
        title={bulkAction ? `${bulkAction.label}: ${selected.length} записей` : ""}
        open={!!bulkAction}
        onCancel={() => setBulkAction(null)}
        okText={bulkAction?.label}
        cancelText="Отмена"
        confirmLoading={bulkRunning}
        onOk={() => bulkAction && runBulk(bulkAction)}
        destroyOnHidden
      >
        <p>Действие выполнится для каждой выбранной записи по очереди; записи с неподходящим статусом будут пропущены, а результат показан по каждой.</p>
        <ul style={{ maxHeight: 220, overflow: "auto", paddingLeft: 20 }}>
          {(list.data?.items ?? []).filter((r) => selected.includes(r.id)).map((r) => (
            <li key={r.id}>
              {rowLabelOf(r)}
              {r.status ? <Tag style={{ marginLeft: 8 }}>{statusLabel(r.status)}</Tag> : null}
            </li>
          ))}
        </ul>
      </Modal>
      <Modal title="Результат по записям" open={!!bulkResults} onCancel={() => setBulkResults(null)} footer={<Button type="primary" onClick={() => { setBulkResults(null); setSelected([]); }}>Готово</Button>} destroyOnHidden width={720}>
        {bulkResults && (
          <>
            <p>
              Успешно: <b>{bulkResults.filter((r) => r.ok).length}</b>, с ошибкой или пропущено: <b>{bulkResults.filter((r) => !r.ok).length}</b>
            </p>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={bulkResults}
              columns={[
                { title: "Запись", dataIndex: "label", width: 200 },
                { title: "Итог", render: (_, r: { ok: boolean; message: string }) => <Tag color={r.ok ? "green" : "red"}>{r.ok ? "выполнено" : "не выполнено"}</Tag> },
                { title: "Подробности", dataIndex: "message" },
              ]}
            />
          </>
        )}
      </Modal>
      <Modal
        title={`Изменить поле у ${selected.length} записей`}
        open={bulkOpen}
        onCancel={() => setBulkOpen(false)}
        okText="Применить"
        cancelText="Отмена"
        confirmLoading={bulkUpdate.isPending}
        onOk={() => bulkForm.validateFields().then((v: { value: unknown }) => bulkField && bulkUpdate.mutate({ [bulkField]: v.value ?? null }))}
        destroyOnHidden
      >
        <Form form={bulkForm} layout="vertical">
          <Form.Item label="Поле">
            <Select
              showSearch
              optionFilterProp="label"
              value={bulkField}
              onChange={(v) => {
                setBulkField(v);
                bulkForm.resetFields();
              }}
              options={editable.map((f) => ({ value: f.name, label: f.label }))}
            />
          </Form.Item>
          {bulkMeta && (
            <Form.Item name="value" label="Новое значение" valuePropName={bulkMeta.type === "boolean" ? "checked" : "value"}>
              {bulkMeta.type === "boolean" ? (
                <Switch />
              ) : bulkMeta.type === "number" ? (
                <InputNumber style={{ width: "100%" }} />
              ) : bulkMeta.type === "ref" && bulkMeta.ref ? (
                <RefSelect entity={bulkMeta.ref} />
              ) : bulkMeta.type === "enum" ? (
                <Select allowClear options={bulkMeta.options ?? []} />
              ) : (
                <Input allowClear />
              )}
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
